import type React from 'react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Heading,
  Icon,
  IconButton,
  Image,
  Modal,
  Paragraph,
  Spinner,
  Tag,
  TextButton,
  UnstyledButton,
} from '@zillow/constellation-10';
import type { ButtonRootPropsInterface } from '@zillow/constellation-10';
import { IconDeleteOutline, IconFileFilled } from '@zillow/constellation-icons';
import {
  useCappsRenterDocumentsWebApiHandler_deleteDocument,
  useCappsRenterDocumentsWebApiHandler_getEditorUrl,
} from '@zg-rentals/ts-capps-client-react-query';
import { css } from '@/styled-system/css';
import type { Document } from '../../../../../models/rental-application';
import { DOCUMENT_POOL_TAG } from '../../../../../constants/applicationsConstants';
import { RENTAL_APPLICATION_TAG } from '../../../../../hooks/useRentalApplication';

function getIsRedactionComplete(document: Document) {
  if (!document.isRedactionCheckComplete) {
    return false;
  }

  if (document.isRedacted) {
    return true;
  }

  if (document.canBeRedacted) {
    return false;
  }

  return true;
}

function DeleteDocumentButton({
  documentId,
  onDeleted,
  onCompleted,
  children,
  ...buttonProps
}: {
  documentId: string;
  onDeleted?: (documentId: string) => void;
  onCompleted?: () => void;
} & React.PropsWithChildren<Omit<ButtonRootPropsInterface, 'onClick'>>) {
  const { mutate: deleteDocument, isPending } = useCappsRenterDocumentsWebApiHandler_deleteDocument({
    meta: {
      invalidates: [RENTAL_APPLICATION_TAG, DOCUMENT_POOL_TAG],
    },
    onSuccess: () => {
      onDeleted?.(documentId);
      onCompleted?.();
    },
  });

  const onClick = () => {
    if (!documentId) {
      return;
    }

    deleteDocument({ postData: { documentId } });
  };

  return (
    <Button {...buttonProps} onClick={onClick} loading={isPending}>
      {children}
    </Button>
  );
}

type Props = {
  document: Document;
  onDeleted?: (documentId: string) => void;
  menu?: (actions: { onDelete: () => void }) => ReactNode;
  deleteConfirmationText?: string;
};

export function DocumentCard({ document, onDeleted, menu, deleteConfirmationText }: Props) {
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isExpired = document.expirationDateTime && new Date(document.expirationDateTime).valueOf() < Date.now();
  const isRedactionComplete = getIsRedactionComplete(document);
  const isDocumentReadyToView = !!document.id && isRedactionComplete && !isExpired;
  const {
    data: editorUrlData,
    isError: isEditorUrlError,
    isFetching: isFetchingEditorUrl,
  } = useCappsRenterDocumentsWebApiHandler_getEditorUrl(
    { documentId: document.id! },
    {
      enabled: isPreviewOpen && isDocumentReadyToView,
      meta: {
        tags: ['cappsRenterDocuments', 'getEditorUrl'],
        context: { api: 'cappsRenterDocuments', details: 'getEditorUrl' },
      },
    },
  );
  const editorUrl = editorUrlData?.documentEditorUrl;

  return (
    <>
      <Card
        elevated={false}
        tone="soft"
        body={
          <>
            {isDocumentReadyToView ? (
              <UnstyledButton
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                aria-label={`Preview ${document.fileName || 'income document'}`}
                css={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
              >
                <Image
                  css={{ height: '2400', objectFit: 'contain' }}
                  src={document.previewUrl as string}
                  alt={`Preview image for ${document.fileName}`}
                />
              </UnstyledButton>
            ) : (
              <Box
                css={{
                  position: 'relative',
                  height: '2400',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isExpired ? (
                  <>
                    <Icon size="lg">
                      <IconFileFilled />
                    </Icon>
                    <Tag tone="warning" size="sm" css={{ position: 'absolute', top: 0, right: 0 }}>
                      Expired
                    </Tag>
                  </>
                ) : (
                  <Spinner />
                )}
              </Box>
            )}
            <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Paragraph
                textStyle="fineprint-bold"
                css={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
              >
                {document?.fileName || `Income document`}
              </Paragraph>
              {menu ? (
                menu({ onDelete: () => setIsDeletingDoc(true) })
              ) : (
                <IconButton
                  disabled={document.id == null}
                  icon={<IconDeleteOutline />}
                  type="button"
                  emphasis="bare"
                  size="xs"
                  title="Delete document"
                  onClick={() => setIsDeletingDoc(true)}
                />
              )}
            </Box>
          </>
        }
      />
      {isPreviewOpen && (
        <Modal.Root open size="fullScreen" onOpenChange={(open) => !open && setIsPreviewOpen(false)}>
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content>
                <Modal.Header>
                  <Heading level={1}>Income document</Heading>
                </Modal.Header>
                <Divider />
                <Modal.Body css={{ padding: '0 !important' }}>
                  {isEditorUrlError && !isFetchingEditorUrl ? (
                    <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '5000' }}>
                      <Paragraph>We couldn’t load this document. Please close this window and try again.</Paragraph>
                    </Box>
                  ) : editorUrl ? (
                    <iframe
                      src={editorUrl}
                      title={`Preview of ${document.fileName || 'income document'}`}
                      className={css({ width: '100%', height: '100%', border: 'none', display: 'block' })}
                    />
                  ) : (
                    <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '5000' }}>
                      <Spinner />
                    </Box>
                  )}
                </Modal.Body>
                {editorUrl && (
                  <>
                    <Divider />
                    <Modal.Footer>
                      <Anchor href={editorUrl} target="_blank" rel="noopener noreferrer">
                        Open in new tab
                      </Anchor>
                    </Modal.Footer>
                  </>
                )}
                <Modal.CloseButton />
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      )}
      {isDeletingDoc && (
        <Modal
          open
          onOpenChange={(open) => !open && setIsDeletingDoc(false)}
          header={<Heading level={2}>Document deletion</Heading>}
          body={
            <>
              <Paragraph>
                Delete {document?.fileName ? <strong>{document?.fileName}</strong> : `this income document`}?
              </Paragraph>
              {deleteConfirmationText && <Paragraph>{deleteConfirmationText}</Paragraph>}
            </>
          }
          footer={
            <ButtonGroup aria-label="Delete income document actions">
              <TextButton onClick={() => setIsDeletingDoc(false)} type="button">
                Cancel
              </TextButton>
              <DeleteDocumentButton
                documentId={document.id!}
                tone="critical"
                type="button"
                onDeleted={onDeleted}
                onCompleted={() => setIsDeletingDoc(false)}
              >
                Yes, delete
              </DeleteDocumentButton>
            </ButtonGroup>
          }
        />
      )}
    </>
  );
}
